import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ZodError } from "zod";
import { isToday, isYesterday, subMonths, subWeeks } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getZodFirstErrorFields<T extends object>(
  error: ZodError<T>
): Partial<Record<keyof T, string>> {
  const fieldErrors = error.flatten().fieldErrors;

  const entries = Object.entries(fieldErrors).reduce((acc, curr) => {
    return {
      ...acc,
      [curr[0]]: (curr[1] as string[] | undefined)?.[0],
    };
  }, {});

  return entries;
}

type Entries<T> = {
  [K in keyof T]-?: [K, T[K]];
}[keyof T][];

export function objectEntries<T extends object>(obj: T): Entries<T> {
  return Object.entries(obj) as Entries<T>;
}

export type GroupedItems<TItem> = {
  today: TItem[];
  yesterday: TItem[];
  lastWeek: TItem[];
  lastMonth: TItem[];
  older: TItem[];
};

export function groupItemsByDate<TItem extends { created_at: string }>(
  items: TItem[]
): GroupedItems<TItem> {
  const now = new Date();
  const oneWeekAgo = subWeeks(now, 1);
  const oneMonthAgo = subMonths(now, 1);

  return items.reduce(
    (groups, item) => {
      const dateItem = new Date(item.created_at);

      if (isToday(dateItem)) {
        groups.today.push(item);
      } else if (isYesterday(dateItem)) {
        groups.yesterday.push(item);
      } else if (dateItem > oneWeekAgo) {
        groups.lastWeek.push(item);
      } else if (dateItem > oneMonthAgo) {
        groups.lastMonth.push(item);
      } else {
        groups.older.push(item);
      }

      return groups;
    },
    {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: [],
    } as GroupedItems<TItem>
  );
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export async function fetcher(url: string) {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      "An error occurred while fetching the data."
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
}

export function getBaseURL() {
  let url =
    process?.env?.NEXT_PUBLIC_SITE_URL ??
    process?.env?.NEXT_PUBLIC_VERCEL_URL ??
    "http://localhost:3000/";
  url = url.startsWith("http") ? url : `https://${url}`;
  url = url.endsWith("/") ? url : `${url}/`;
  return url;
}

export function sleep(seconds: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, seconds * 1000);
  });
}

/**
 * Formats bytes into human readable format.
 * @param bytes Number of bytes.
 * @param opts Options for formatting.
 * @param opts.decimals Number of decimal places. Defaults to 0.
 * @param opts.sizeType "accurate" for KiB, MiB, GiB, TiB or "normal" for KB, MB, GB, TB. Defaults to "normal".
 * @returns Formatted string with unit.
 */
export const formatBytes = (bytes: number, opts: { decimals?: number, sizeType?: "accurate" | "normal" } = {}) => {
  const { decimals = 0, sizeType = "normal" } = opts
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"]
  if (bytes === 0) return "0 Byte"
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${sizeType === "accurate" ? (accurateSizes[i] ?? "Bytes") : (sizes[i] ?? "Bytes")}`
}